import React from 'react';
import { VisualAid } from '../types';
import { MetricStairsCompact } from './MetricStairsCompact';
import { FractionVisual } from './FractionVisual';
import { GeoGrid } from './GeoGrid';
import { NumberLine } from './NumberLine';
import { RatioTable } from './RatioTable';
import { RuleCard } from './RuleCard';
import { MoneyVisual } from './MoneyVisual';
import { ColumnVisual } from './ColumnVisual';
import { AnalogClock } from './AnalogClock';
import { KofschipMnemonic } from './KofschipMnemonic';
import { SyllableBlocks } from './SyllableBlocks';
import { VerbTable } from './VerbTable';
import { CircleVisual } from './CircleVisual';
import { CoordinateGrid } from './CoordinateGrid';
import { Box3D } from './Box3D';
import { CompositeShape } from './CompositeShape';
import { resolveFractionVisualType } from '../fractionVisualTypeResolver';
import { warn } from '../../utils/logger';
export const VISUAL_AID_FACTORY_SUPPORTED_TYPES = ['stairs', 'fraction', 'geo', 'decimalgrid', 'numberline', 'ratio', 'rule', 'money', 'column', 'clock', 'kofschipMnemonic', 'syllableBlocks', 'verbTable', 'circle', 'coordinateGrid', 'box3d', 'compositeShape'] as const;
export const SUPPORTED_VISUAL_AID_TYPES = VISUAL_AID_FACTORY_SUPPORTED_TYPES;
interface VisualAidFactoryProps {
  visualAid: VisualAid;
  isCorrect?: boolean;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
type DataRecord = Record<string, unknown>;
function getString(data: DataRecord, key: string): string | undefined {
  const v = data[key];
  return typeof v === 'string' ? v : undefined;
}
function getNumber(data: DataRecord, key: string): number | undefined {
  const v = data[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}
function getBoolean(data: DataRecord, key: string): boolean | undefined {
  const v = data[key];
  return typeof v === 'boolean' ? v : undefined;
}
function getRecord(data: DataRecord, key: string): Record<string, unknown> | undefined {
  const v = data[key];
  return v && typeof v === 'object' ? v as Record<string, unknown> : undefined;
}
function getArray(data: DataRecord, key: string): unknown[] | undefined {
  const v = data[key];
  return Array.isArray(v) ? v : undefined;
}
/**
 * Helper to determine if a visual aid should be shown based on correctness state
 */
export function shouldShowVisualAid(visualAid?: VisualAid, isCorrect?: boolean): boolean {
  if (!visualAid || !visualAid.type || !visualAid.data) return false;
  const {
    showWhen
  } = visualAid;
  // 'always' is default if not specified
  if (!showWhen || showWhen === 'always') return true;
  if (showWhen === 'correct') return isCorrect === true;
  if (showWhen === 'wrong') return isCorrect === false;
  return false;
}
export function VisualAidFactory({
  visualAid,
  isCorrect,
  mode = 'support'
}: VisualAidFactoryProps) {
  if (!shouldShowVisualAid(visualAid, isCorrect)) return null;
  const {
    data
  } = visualAid;
  // Normalize type aliases to canonical names
  let type = visualAid.type as string;
  if (type === 'numberLine') type = 'numberline';
  if (type === 'moneyCoins') type = 'money';
  if (type === 'decimalGrid') type = 'decimalgrid';
  if (type === 'analog_clock') type = 'clock';
  // fraction_bar and fraction_circle are legacy aliases — both map to 'fraction'
  if (type === 'fraction_bar' || type === 'fraction_circle') type = 'fraction';
  try {
    switch (type) {
      case 'stairs':
        {
          const stairsType = getString(data, 'type');
          if (!stairsType) return null;
          const from = getString(data, 'from');
          const to = getString(data, 'to');
          const value = getNumber(data, 'value') ?? 0;
          const highlight = getBoolean(data, 'highlight') ?? false;
          const showAnimation = getBoolean(data, 'showAnimation') ?? false;
          return <MetricStairsCompact type={stairsType as any} from={from as any} to={to as any} value={value} highlight={highlight} showAnimation={showAnimation} mode={mode} data-id="element-6102" />;
        }
      case 'circle':
        {
          const radius = getNumber(data, 'radius');
          const diameter = getNumber(data, 'diameter');
          const circumference = getNumber(data, 'circumference');
          const area = getNumber(data, 'area');
          const label = getString(data, 'label');
          const showFormula = getBoolean(data, 'showFormula') ?? false;
          const highlightRadius = getBoolean(data, 'highlightRadius') ?? false;
          const highlightDiameter = getBoolean(data, 'highlightDiameter') ?? false;
          return <CircleVisual radius={radius} diameter={diameter} circumference={circumference} area={area} label={label} showFormula={showFormula} highlightRadius={highlightRadius} highlightDiameter={highlightDiameter} mode={mode} data-id="element-6103" />;
        }
      case 'fraction':
        {
          const denominator = getNumber(data, 'denominator') ?? 4;
          if (denominator === 0) return null;
          const value = getNumber(data, 'value') ?? 0;
          const numerator = getNumber(data, 'numerator') ?? Math.round(value * denominator);
          const label = getString(data, 'label');
          const showEaten = getBoolean(data, 'showEaten') ?? false;
          const operator = getString(data, 'operator') as '+' | '-' | '×' | '÷' ?? '+';
          // Check for multi-fraction mode (for addition/subtraction)
          const fractionsArray = getArray(data, 'fractions') as Array<{
            numerator: number;
            denominator: number;
            label?: string;
            visualType?: string;
          }> | undefined;
          // Resolve visual type: only 'pizza' | 'bar' are supported
          const rawVisualType = getString(data, 'visualType');
          const explicitType = rawVisualType === 'pizza' || rawVisualType === 'bar' ? rawVisualType as 'pizza' | 'bar' : undefined;
          const questionText = getString(data, 'questionText') ?? '';
          const options = getArray(data, 'options') as string[] | undefined;
          const visualType = resolveFractionVisualType(questionText, options, explicitType);
          return <FractionVisual numerator={numerator} denominator={denominator} label={label} type={visualType} showEaten={showEaten} mode={mode} fractions={fractionsArray} operator={operator} data-id="element-6104" />;
        }
      case 'geo':
        {
          const width = getNumber(data, 'width');
          const height = getNumber(data, 'height');
          if (!width || !height) return null;
          const depth = getNumber(data, 'depth');
          const label = getString(data, 'label');
          const showGrid = getBoolean(data, 'showGrid');
          const unit = getString(data, 'unit');
          return <GeoGrid width={width} height={height} depth={depth} mode={mode} label={label} showGrid={showGrid} unit={unit} data-id="element-6105" />;
        }
      case 'decimalgrid':
        {
          // Decimal grid: renders as a GeoGrid with decimal-specific defaults
          const cols = getNumber(data, 'cols') ?? getNumber(data, 'width') ?? 10;
          const rows = getNumber(data, 'rows') ?? getNumber(data, 'height') ?? 10;
          const highlighted = getNumber(data, 'highlighted') ?? getNumber(data, 'value') ?? 0;
          const label = getString(data, 'label');
          return <GeoGrid width={cols} height={rows} highlighted={highlighted} label={label} mode={mode} data-id="element-6106" />;
        }
      case 'numberline':
        return <NumberLine {...data as any} mode={mode} data-id="element-6107" />;
      case 'ratio':
        {
          const headers = getArray(data, 'headers');
          const rows = getArray(data, 'rows');
          if (!headers || !rows) return null;
          return <RatioTable {...data as any} mode={mode} data-id="element-6108" />;
        }
      case 'rule':
        {
          const title = getString(data, 'title');
          const content = getString(data, 'content');
          if (!title || !content) return null;
          return <RuleCard {...data as any} data-id="element-6109" />;
        }
      case 'money':
        {
          const amount = getNumber(data, 'amount');
          if (amount === undefined) return null;
          const breakdown = getRecord(data, 'breakdown');
          const showCoins = data['showCoins'] !== false;
          const showBills = data['showBills'] !== false;
          const interactive = getBoolean(data, 'interactive') ?? false;
          const label = getString(data, 'label');
          return <MoneyVisual amount={amount} breakdown={breakdown as any} showCoins={showCoins} showBills={showBills} interactive={interactive} label={label} data-id="element-6110" />;
        }
      case 'column':
        {
          const operation = getString(data, 'operation');
          if (!operation) return null;
          const operand1 = (data as any).operand1 ?? getArray(data, 'numbers')?.[0];
          const operand2 = (data as any).operand2 ?? getArray(data, 'numbers')?.[1];
          if (operand1 === undefined || operand2 === undefined) return null;
          const showSteps = data['showSteps'] !== false;
          const currentStep = getNumber(data, 'currentStep') ?? 0;
          const animated = data['animated'] !== false;
          return <ColumnVisual operation={operation as any} operand1={operand1} operand2={operand2} showSteps={showSteps} currentStep={currentStep} animated={animated} onStepChange={(data as any).onStepChange} data-id="element-6111" />;
        }
      case 'clock':
        {
          const hours = getNumber(data, 'hours') ?? getNumber(data, 'hour') ?? 12;
          const minutes = getNumber(data, 'minutes') ?? getNumber(data, 'minute') ?? 0;
          const label = getString(data, 'label');
          return <div className="flex flex-col items-center gap-3" data-id="element-6112">
            <AnalogClock hours={hours} minutes={minutes} showSeconds={false} size={160} data-id="element-6113" />

            {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-6114">
                {label}
              </p>}
          </div>;
        }
      case 'kofschipMnemonic':
        {
          const verb = getString(data, 'verb') ?? '';
          const stem = getString(data, 'stem') ?? '';
          const lastLetter = getString(data, 'lastLetter') ?? '';
          const useTE = getBoolean(data, 'useTE') ?? false;
          const label = getString(data, 'label');
          return <KofschipMnemonic verb={verb} stem={stem} lastLetter={lastLetter} useTE={useTE} label={label} mode={mode} data-id="element-6115" />;
        }
      case 'syllableBlocks':
        {
          const word = getString(data, 'word') ?? '';
          const syllables = getArray(data, 'syllables') as string[] | undefined;
          const types = getArray(data, 'types') as ('open' | 'closed')[] | undefined;
          const highlight = getNumber(data, 'highlight');
          const label = getString(data, 'label');
          if (!syllables || !types) return null;
          return <SyllableBlocks word={word} syllables={syllables} types={types} highlight={highlight} label={label} data-id="element-6116" />;
        }
      case 'verbTable':
        {
          const verb = getString(data, 'verb') ?? '';
          const tense = getString(data, 'tense') as any ?? 'present';
          const persons = getArray(data, 'persons') as string[] | undefined;
          const forms = getArray(data, 'forms') as string[] | undefined;
          const highlight = getString(data, 'highlight');
          const label = getString(data, 'label');
          if (!persons || !forms) return null;
          return <VerbTable verb={verb} tense={tense} persons={persons} forms={forms} highlight={highlight} label={label} data-id="element-6117" />;
        }
      case 'coordinateGrid':
        {
          const width = getNumber(data, 'width') ?? 10;
          const height = getNumber(data, 'height') ?? 10;
          const points = getArray(data, 'points') as Array<{
            x: number;
            y: number;
            label: string;
            color?: string;
          }> | undefined;
          const label = getString(data, 'label');
          const showGrid = getBoolean(data, 'showGrid') ?? true;
          return <CoordinateGrid width={width} height={height} points={points} label={label} showGrid={showGrid} mode={mode} data-id="element-6118" />;
        }
      case 'box3d':
        {
          const length = getNumber(data, 'length') ?? getNumber(data, 'width') ?? 5;
          const width = getNumber(data, 'depth') ?? getNumber(data, 'width') ?? 5;
          const height = getNumber(data, 'height') ?? 5;
          const unit = getString(data, 'unit') ?? 'cm';
          const label = getString(data, 'label');
          const showGrid = getBoolean(data, 'showGrid') ?? true;
          return <Box3D length={length} width={width} height={height} unit={unit} label={label} showGrid={showGrid} mode={mode} data-id="element-6119" />;
        }
      case 'compositeShape':
        {
          const rectangles = getArray(data, 'rectangles') as Array<{
            x: number;
            y: number;
            width: number;
            height: number;
            label?: string;
          }> | undefined;
          if (!rectangles || rectangles.length === 0) return null;
          const unit = getString(data, 'unit') ?? 'cm';
          const label = getString(data, 'label');
          const showGrid = getBoolean(data, 'showGrid') ?? true;
          const showDimensions = getBoolean(data, 'showDimensions') ?? true;
          return <CompositeShape rectangles={rectangles} unit={unit} label={label} showGrid={showGrid} showDimensions={showDimensions} mode={mode} data-id="element-6120" />;
        }
      default:
        warn(`Unknown visual aid type: ${type}`);
        return null;
    }
  } catch (error) {
    warn('Error rendering visual aid:', error);
    return null;
  }
}